import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { Divider } from "@mui/material";
import moment from "moment";
import { AddIcon, NotInterestedIcon, DoneOutlineIcon, SearchIcon } from "../../../Resources/Icons/icons";
import { GetMakeModelCombo, UpdateMakeModelCombo, GetUserFindbyId } from "../../../Service/_index";
import { Transition } from "../../../UiComponents/Transition/Transition";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import ConfirmBox from "../../../UiComponents/ConfirmBox/ConfirmBox";
import TabFilter from "../../../Components/Tabs/Tabs";
import DisabledImage from "../../../../src/Resources/Images/disable.png";
import ActiveImage from "../../../../src/Resources/Images/ActiveImage.png";
import AddMakeModelCombo, { rowName } from "./AddMakeModelCombo";

const FilterOption = [
  { label: "All Data", isEnabled: "all", value: 1 },
  { label: "ActiveData", isEnabled: "true", value: 2 },
  { label: "In ActiveData", isEnabled: "false", value: 3 },
];

const getClassName = (data) =>
  data.status === "Active" ? "Active-chip" : data.status === "Inactive" ? " inActive-chip" : "";

const headCells = [
  { id: "displayName", label: "Display Name", placeMent: true },
  { id: "odCompany", label: "OD Company", placeMent: true },
  { id: "odName", label: "OD Make and Model", placeMent: true },
  { id: "tpCompany", label: "TP Company", placeMent: true },
  { id: "tpName", label: "TP Make and Model", placeMent: true },
  { id: "createdAt", label: "Created Date", placeMent: true },
  { id: "attention", label: "Check", placeMent: true },
  { id: "status", label: "Status", placeMent: true, class: getClassName },
];

const describe = (row) => (row ? `${rowName(row)} (${row.cc || "-"} cc)` : "Deleted");

/** The table row for one combined record. A broken pairing says WHAT is wrong, on hover. */
const toTableRow = (combo) => ({
  ...combo,
  odName: describe(combo.odRow),
  tpName: describe(combo.tpRow),
  createdAt: combo.createdAt ? moment(combo.createdAt).format("DD/MM/YYYY hh:mm A") : "-",
  status: combo.isEnabled ? "Active" : "Inactive",
  attention: combo.usable ? (
    "OK"
  ) : (
    <Tooltip
      title={
        <div>
          {(combo.issues || []).map((issue, i) => (
            <div key={i}>• {issue.message}</div>
          ))}
        </div>
      }
    >
      <Chip size="small" color="warning" label="Needs attention" />
    </Tooltip>
  ),
});

const MakeModelCombo = () => {
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [selectedData, setSelectedData] = React.useState({});
  const [combos, setCombos] = React.useState([]);
  const [switchedOff, setSwitchedOff] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1);
  const [openConfirmBox, setOpenConfirmBox] = React.useState(false);
  const [activeBoolean, setActiveBoolean] = useState(true);
  const [searchText, setSearchText] = useState("");
  // Only the owner (CLIENT login) manages combined records. Everyone else gets a
  // read-only list: the server already refuses their POST / PUT, this just stops
  // the page offering what would be refused.
  const isMasterAdmin = String(sessionStorage.getItem("userType") || "").toUpperCase() === "CLIENT";
  // The combos the admin assigned to this user (Edit User > Combined Make & Model).
  const [assignedComboIds, setAssignedComboIds] = useState(new Set());
  const tableConfig = isMasterAdmin
    ? {
        isAction: true,
        isEdit: true,
        isMultiDelete: false,
        isSearch: false,
        isSwitch: true,
        historyEntity: "makeModelCombo",
      }
    : { isAction: false, isEdit: false, isMultiDelete: false, isSearch: false, isSwitch: false };

  React.useEffect(() => {
    if (isMasterAdmin) return;
    const userId = sessionStorage.getItem("UserId");
    if (!userId) return;
    GetUserFindbyId(userId).then((res) => {
      setAssignedComboIds(
        new Set(
          (res?.data?.makemodelcomboids || [])
            .filter((row) => row?.makeModelCombo)
            .map((row) => String(row.makeModelCombo))
        )
      );
    });
  }, []);

  // The tab decides which records the server returns; reloading keeps it. A
  // read-only user fetches them all and the tab is applied below, so "does this
  // user have assignments" is judged against the whole list, not one tab of it.
  const load = React.useCallback((tab = selectedTab) => {
    const wanted = FilterOption.find((option) => option.value === tab);
    const query =
      !isMasterAdmin || wanted?.isEnabled === "all" ? {} : { isEnabled: wanted?.isEnabled === "true" };
    setLoading(true);
    GetMakeModelCombo(query)
      .then((res) => {
        if (!res) return;
        setSwitchedOff(res.enabled === false);
        setCombos((res.data || []).map(toTableRow));
      })
      .finally(() => setLoading(false));
  }, [selectedTab]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // What the table shows. For the owner that is whatever the server returned for
  // the tab. For a user: only their assigned combos (the same rule as the Online
  // Policy dropdown — an assignment to a combo that no longer exists does not
  // empty the page), then the tab, applied here.
  const visibleRows = React.useMemo(() => {
    let rows = combos;
    if (!isMasterAdmin) {
      const restrict = rows.some((row) => assignedComboIds.has(String(row._id)));
      if (restrict) rows = rows.filter((row) => assignedComboIds.has(String(row._id)));
      const tab = FilterOption.find((option) => option.value === selectedTab)?.isEnabled;
      if (tab === "true") rows = rows.filter((row) => row.isEnabled);
      if (tab === "false") rows = rows.filter((row) => !row.isEnabled);
    }
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      [row.displayName, row.odCompany, row.tpCompany, row.odName, row.tpName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [combos, assignedComboIds, searchText, selectedTab, isMasterAdmin]);

  const toggle = (row, active) => {
    setSelectedData(row);
    setActiveBoolean(!active);
    setOpenConfirmBox(true);
  };

  const confirmToggle = () => {
    UpdateMakeModelCombo(selectedData._id, {
      isEnabled: activeBoolean,
      updatedAt: new Date(),
      updatedBy: sessionStorage.getItem("UserId"),
    }).then(() => load());
    setOpenConfirmBox(false);
  };

  return (
    <>
      <Grid container className="Master_Card_Container">
        <Grid container alignItems={"center"} className="Master_Card_Sub_Container">
          <Grid item xs={12} sm={6} md={6}>
            <Typography className="Master_Card_Tittle">Combined Make and Model</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6} className="d-flex justify-content-end">
            {isMasterAdmin ? (
              <Button
                className="Master_Card_create_Button w-100"
                startIcon={<AddIcon />}
                disabled={switchedOff}
                onClick={() => {
                  setSelectedData({});
                  setOpenDrawer(true);
                }}
              >
                Create
              </Button>
            ) : null}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1 }} />
        </Grid>
        {switchedOff ? (
          <Grid item xs={12} sx={{ p: 2 }}>
            <Typography sx={{ fontSize: "13px", color: "#B54708", background: "#FFFAEB", border: "1px solid #FEDF89", borderRadius: "8px", p: 1.5 }}>
              {isMasterAdmin
                ? "Combined Make and Model is switched off (ENABLE_MULTI_PRODUCT_POLICY). Nothing can be created until it is turned on."
                : "Combined Make and Model is switched off."}
            </Typography>
          </Grid>
        ) : null}
        <Grid
          container
          alignItems={"center"}
          justifyContent={"space-between"}
          className="Master_Card_Sub_Container"
        >
          <TabFilter options={FilterOption} selectedTab={selectedTab} onTabChange={handleTabChange} />
          <Grid item xs={12} sm={4} md={3.1} mt={{ xs: 2, sm: 0, md: 0 }}>
            <Input
              focused="false"
              className="w-100 Master_Card_Input"
              onChange={(e) => setSearchText(e.target.value)}
              disableUnderline={true}
              placeholder="Search in Combined Make and Model"
              startAdornment={
                <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                  <IconButton aria-label="search">
                    <SearchIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
        </Grid>
        <MasterCommonTable
          tableConfig={tableConfig}
          data={visibleRows}
          columns={headCells}
          handleEdit={(row) => {
            setSelectedData(row);
            setOpenDrawer(true);
          }}
          handleApprove={toggle}
          tableTotalCount={visibleRows.length}
          loading={loading}
        />
      </Grid>

      {isMasterAdmin ? (
      <>
      <Dialog
        open={openDrawer}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="alert-dialog-slide-description"
        PaperProps={{
          sx: { maxWidth: { xs: "90%", sm: "80%", md: "60%", lg: "50%" } },
        }}
      >
        {openDrawer ? (
          <AddMakeModelCombo
            title={selectedData._id ? "Edit" : "Create"}
            selectedData={selectedData}
            formType={selectedData._id ? "edit" : "add"}
            setOpenDrawer={setOpenDrawer}
            GetData={() => load()}
          />
        ) : null}
      </Dialog>

      <ConfirmBox
        open={openConfirmBox}
        title={selectedData.isEnabled ? "Inactive" : "Active"}
        content={`Are you sure want to ${selectedData.isEnabled ? "Inactive" : "Active"}`}
        confirmButton={selectedData.isEnabled ? "Inactive" : "Active"}
        setOpenConfirmBox={setOpenConfirmBox}
        Function={confirmToggle}
        img={selectedData.isEnabled ? DisabledImage : ActiveImage}
        icon={selectedData.isEnabled ? <NotInterestedIcon /> : <DoneOutlineIcon />}
        color={selectedData.isEnabled ? "error" : "success"}
      />
      </>
      ) : null}
    </>
  );
};

export default MakeModelCombo;
